import {buildHierarchy, HierarchyEmployee, HierarchyRoot} from "../util/EmployeeHierarchy";
import {useEmployeeStore} from "./EmployeeStore";
import {useMemo} from "react";
import {INeighbourhood} from "./Model";
import {RemoteValue} from "../util/RemoteValue";
import {EmployeeNeighbourhood} from "./EmployeeNeighbourhoodStore";
import {TeamTree, useTeamTreeStore} from "./TeamTreeStore";
import {useWorkSiteMapStore, WorkSites} from "./WorkSiteMapStore";
import {useEmployeeKeyHistoryStore} from "./EmployeeKeyHistoryStore";
import {AssignedDesks, useAssignedDeskStore} from "./AssignedDeskStore";
import {usePermission} from "../util/access/UseAccess";
import {Desks_Read} from "../util/access/Permission";

const emptyAssignedDesks = RemoteValue.fromValue(new AssignedDesks([]));

export function useEmployeeHierarchyStore<Hood extends INeighbourhood>(employeeNeighbourhoods: RemoteValue<EmployeeNeighbourhood<Hood>>): RemoteValue<{
    globalHierarchy: HierarchyRoot,
    teamTree: TeamTree,
    workSites: WorkSites,
    employees: HierarchyEmployee[],
    length: number,
    employeeNeighbourhoods: EmployeeNeighbourhood<Hood>,
}> {
    const employees = useEmployeeStore(state => state.remoteValue);
    const employeeKeyHistory = useEmployeeKeyHistoryStore(state => state.remoteValue);
    const teamTree = useTeamTreeStore(state => state.remoteValue);
    const workSites = useWorkSiteMapStore(state => state.remoteValue);
    const assignedDesksValue = useAssignedDeskStore(state => state.remoteValue);
    const {canReadDesks} = usePermission({canReadDesks: Desks_Read});
    const assignedDesks = canReadDesks ? assignedDesksValue : emptyAssignedDesks;

    return useMemo(() => RemoteValue
            .combine({teamTree, workSites, employeeKeyHistory, employeeNeighbourhoods, employees, assignedDesks})
            .map(({teamTree, workSites, employeeKeyHistory, employeeNeighbourhoods, employees, assignedDesks}) => {
                const {root: globalHierarchy, employees: hierarchyEmployees} = buildHierarchy(
                    employees,
                    employeeNeighbourhoods,
                    teamTree,
                    workSites,
                    employeeKeyHistory,
                    assignedDesks,
                );
                return {
                    globalHierarchy,
                    employeeNeighbourhoods,
                    teamTree,
                    workSites,
                    employees: hierarchyEmployees,
                    length: employees.length,
                };
            }),
        [employees, teamTree, workSites, employeeNeighbourhoods, employeeKeyHistory, assignedDesks],
    );
}
